import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Custom DS font-size scale (theme.css `--text-*`). Default tailwind-merge does
 * not recognise these, so it groups them with `text-pb-*` colours and silently
 * drops one when both are merged through {@link cn} (PB-246). Registering them
 * as font-sizes (below) keeps both. This list MUST cover every custom
 * `--text-*` base token in theme.css — the drift guard in
 * `test/unit/utils.spec.ts` fails if a new one is left unregistered.
 */
export const CUSTOM_FONT_SIZES = [
  "dense-1",
  "dense-2",
  "dense-3",
  "display",
  "heading-1",
  "heading-2",
  "heading-3",
  "title",
  "body",
  "body-small",
  "body-large",
  "caption",
  "label",
  "code",
] as const;

/**
 * Motion utilities (ADR-0091). Registered so a consumer override actually wins:
 * tailwind-merge knows nothing about these names, so without a group a caller
 * passing `duration-motion-fast` to a component that already sets
 * `duration-motion-state` keeps BOTH classes and the winner is source order in
 * the compiled stylesheet — which is not stable across rebuilds.
 */
export const CUSTOM_MOTION_DURATIONS = [
  "duration-motion-fast",
  "duration-motion-state",
  "duration-motion-overlay",
  "duration-motion-layout",
  "duration-motion-camera",
] as const;

export const CUSTOM_MOTION_EASINGS = [
  "ease-motion-standard",
  "ease-motion-enter",
  "ease-motion-exit",
] as const;

/** Whole-behaviour motion classes — one per surface kind, mutually exclusive. */
export const CUSTOM_MOTION_BEHAVIOURS = [
  "pb-motion-overlay",
  "pb-motion-tooltip",
  "pb-motion-banner",
  "pb-motion-rail",
  "pb-motion-swap",
  "pb-motion-scrim",
  "pb-motion-content-enter",
  "pb-enter-row",
  "pb-enter-col",
  "pb-exit-row",
  "pb-exit-col",
  "pb-disclosure",
  "pb-slider-indicator",
  "pb-control-mark",
] as const;

/** Where an overlay travels from — exactly one per overlay. */
export const CUSTOM_MOTION_ORIGINS = [
  "pb-motion-origin-dialog",
  "pb-motion-origin-dialog-panel",
  "pb-motion-origin-anchored",
  "pb-motion-origin-anchored-panel",
  "pb-motion-origin-docked-panel",
  "pb-motion-origin-sheet-end",
  "pb-motion-origin-sheet-bottom",
] as const;

/**
 * State-free custom background-image utilities. Registration currently
 * collapses duplicates; same-modifier conflicts with future gradient utilities
 * also resolve last-wins. Cross-modifier image ownership remains review-owned.
 */
export const CUSTOM_BG_IMAGES = [
  "pb-overlay-paint",
  "pb-overlay-paint-strong",
  "pb-shimmer",
] as const;

// Teach tailwind-merge the custom font-size scale so a `text-<size>` and a
// `text-pb-*` colour land in DIFFERENT conflict groups and both survive a merge
// (PB-246). Built ONCE at module load — its internal cache is reused per call.
const twMerge = extendTailwindMerge<"pb-motion-behaviour" | "pb-motion-origin">(
  {
    extend: {
      classGroups: {
        "font-size": [{ text: [...CUSTOM_FONT_SIZES] }],
        "bg-image": [...CUSTOM_BG_IMAGES],
        duration: [...CUSTOM_MOTION_DURATIONS],
        ease: [...CUSTOM_MOTION_EASINGS],
        "pb-motion-behaviour": [...CUSTOM_MOTION_BEHAVIOURS],
        "pb-motion-origin": [...CUSTOM_MOTION_ORIGINS],
        shadow: [
          {
            shadow: [
              "glow-accent",
              "glow-accent-rest",
              "glow-iris",
              "glow-selected",
            ],
          },
        ],
      },
    },
  },
);

/**
 * Concatenate Tailwind class names while resolving conflicts.
 * Last-write-wins for conflicting utilities (e.g. `cn('p-2', 'p-4')` → `'p-4'`).
 *
 * Used everywhere in `@playbookai/ui` — primitives, shell, widgets — so we
 * never grow a parallel utility helper.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
